import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import styles from './css/index.module.scss';
import PenIcon from '../../../../assets/icons/penMedium.svg';
import SaveIcon from '../../../../assets/icons/save.svg';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import useProjects from '../../../../hooks/useProjects';
import getProjectNameApiError from '../../../../helpers/getProjectNameApiError';
import { closeModalAtom } from '../../../../atoms/modalAtoms';
// import { openConfirmModalAtom } from '../../../../atoms/modalAtoms';
import MiddleEllipsisText from '../../../../components/MiddleEllipsisText';

function ProjectName({ project }) {
  const { t } = useTranslations();
  const { renameProject } = useProjects();
  // const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(project?.name || '');
  const [error, setError] = useState('');
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!isEditing) setName(project?.name || '');
  }, [project?.name, isEditing]);

  const onNameChange = (event) => {
    setName(event.target.value);
    if (error) setError('');
  };

  const stopEditing = () => {
    setError('');
    setName(project?.name || '');
    setIsEditing(false);
  };

  const saveName = async (nextName) => {
    const trimmedName = nextName.trim();
    if (trimmedName.length < 2) {
      setName(trimmedName);
      setError(t(DICTIONARY.minimum2symbols));
      setIsEditing(true);
      closeModal();
      return;
    }

    isSavingRef.current = true;
    const data = await renameProject({ id: project.id, name: trimmedName });
    isSavingRef.current = false;
    const apiError = getProjectNameApiError(data);
    if (data?.status !== 'ok') {
      setName(trimmedName);
      setError(apiError || t(DICTIONARY.error));
      setIsEditing(true);
      closeModal();
      return;
    }

    setError('');
    setIsEditing(false);
    closeModal();
  };

  const onSave = () => {
    if (isSavingRef.current) return;
    saveName(name);
  };

  // Leaving the field closes editing. A changed name is saved right away.
  const onEditorBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    // The whole browser window lost focus (another tab or app, the address bar): keep editing.
    if (!document.hasFocus()) return;
    // Enter already sent this name.
    if (isSavingRef.current) return;

    const trimmedName = name.trim();
    if (trimmedName === (project?.name || '').trim()) {
      stopEditing();
      return;
    }

    saveName(trimmedName);

    // Save or revert from a modal. Turned off for now: the name is saved without asking.
    // setIsEditing(false);
    // openConfirmModal({
    //   text: t(DICTIONARY.unsavedProjectName),
    //   buttonText: t(DICTIONARY.save),
    //   secondButtonText: t(DICTIONARY.revert),
    //   onButtonClick: () => saveName(trimmedName),
    //   onSecondButtonClick: () => {
    //     stopEditing();
    //     closeModal();
    //   },
    // });
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') onSave();
  };

  const onSaveMouseDown = (event) => {
    // Keep the field focused so this click saves instead of opening the blur modal.
    event.preventDefault();
  };

  if (!project?.name && !isEditing) return null;

  return (
    <div className={styles.projectName}>
      {isEditing ? (
        <div className={styles.editor} onBlur={onEditorBlur}>
          <label className={classNames(styles.field, { [styles.fieldError]: error })}>
            <span className={styles.sizer}>{name || ' '}</span>
            <input
              className={classNames(styles.input, { [styles.inputError]: error })}
              value={name}
              onChange={onNameChange}
              onKeyDown={onKeyDown}
              spellCheck={false}
              size={1}
              autoFocus
            />
          </label>
          <button
            type="button"
            className={classNames(styles.iconButton, 'pressable')}
            onMouseDown={onSaveMouseDown}
            onClick={onSave}
          >
            <img src={SaveIcon} alt="" />
          </button>
        </div>
      ) : (
        <div className={styles.view}>
          <MiddleEllipsisText className={styles.title} text={project.name} onClick={() => setIsEditing(true)} />
          <button
            type="button"
            className={classNames(styles.iconButton, 'pressable')}
            onClick={() => setIsEditing(true)}
          >
            <img src={PenIcon} alt="" />
          </button>
        </div>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

export default ProjectName;
